import React, { useState } from 'react';
import { Lock, Shield, Crosshair, Trash2, Bolt, CheckCircle, ChevronDown, ChevronUp, AlertTriangle, HardDrive, Cpu, FileText, HelpCircle, Info } from 'lucide-react';

const SecureEraseHelpCenter = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('algorithms');
  const [expandedAlgorithm, setExpandedAlgorithm] = useState(null);
  const [expandedRisk, setExpandedRisk] = useState(null);
  const [expandedPractice, setExpandedPractice] = useState(null);

  const deleteModesConfig = {
    standard: { 
      passes: 1, 
      color: 'bg-blue-500', 
      label: 'Standard Secure Delete',
      icon: <Lock size={18} />,
      level: 'N',
      description: 'Single pass overwrite suitable for non-sensitive data',
      details: 'Performs a single pass of zeros over the data area. Fast but provides only basic protection against simple recovery tools.',
      useCases: [
        'Temporary files',
        'Browser cache',
        'Non-confidential documents'
      ],
      limitations: [
        'Not suitable for sensitive data',
        'Can be recovered with advanced tools',
        'Does not meet regulatory requirements'
      ]
    },
    military: { 
      passes: 3, 
      color: 'bg-red-500', 
      label: 'DoD 5220.22-M',
      icon: <Shield size={18} />,
      level: 'M',
      description: 'U.S. Department of Defense standard 3-pass method',
      details: 'Overwrites data with zeros, then with ones, and finally with random patterns. Meets U.S. DoD 5220.22-M standard for clearing magnetic media.',
      useCases: [
        'Financial records',
        'Personal information',
        'Business documents',
        'Healthcare data'
      ],
      limitations: [
        'Less effective on SSDs',
        'May not meet all international standards',
        'Slower than single pass'
      ],
      compliance: ['DoD 5220.22-M', 'NIST SP 800-88 (Clear)']
    },
    forensic: { 
      passes: 7, 
      color: 'bg-purple-500', 
      label: 'Forensic-Level Overwrite',
      icon: <Crosshair size={18} />,
      level: 'F',
      description: '7-pass method designed to thwart forensic recovery',
      details: 'Performs 7 passes with specific patterns designed to defeat advanced forensic recovery techniques. Based on the Peter Gutmann 35-pass method but optimized for modern drives.',
      useCases: [
        'Legal documents',
        'Corporate secrets',
        'Government data',
        'Forensic cleanup'
      ],
      limitations: [
        'Time consuming',
        'Still may not work on SSDs',
        'Overkill for most use cases'
      ],
      compliance: ['HIPAA', 'GDPR', 'SOX']
    },
    gutmann: { 
      passes: 35, 
      color: 'bg-yellow-500', 
      label: 'Gutmann Method',
      icon: <Trash2 size={18} />,
      level: 'G',
      description: '35-pass extreme security method for magnetic media',
      details: 'The most thorough software-based deletion method. Originally designed for older MFM/RLL encoded drives, it remains effective against sophisticated recovery attempts on modern drives.',
      useCases: [
        'Top secret information',
        'Cryptographic keys',
        'Data requiring maximum security',
        'Media disposal'
      ],
      limitations: [
        'Extremely time consuming',
        'Diminishing returns on modern drives',
        'May reduce drive lifespan',
        'Not recommended for SSDs'
      ],
      compliance: ['NSA/CSS Storage Device Declassification Manual']
    },
    nist: { 
      passes: 3, 
      color: 'bg-green-500', 
      label: 'NIST 800-88',
      icon: <Bolt size={18} />,
      level: 'N',
      description: 'NIST recommended 3-pass method with verification',
      details: 'Implements the NIST Special Publication 800-88 guidelines for media sanitization. Includes verification pass to confirm data was overwritten.',
      useCases: [
        'Government systems',
        'Regulated industries',
        'When NIST compliance is required'
      ],
      limitations: [
        'Verification adds time',
        'SSD limitations apply',
        'Specific patterns not defined'
      ],
      compliance: ['NIST SP 800-88', 'FIPS 199']
    }
  };

  const riskFactors = [
    {
      id: 'ssd',
      title: 'SSD & Flash Storage Limitations',
      severity: 'high',
      icon: <HardDrive size={18} />,
      summary: 'SSDs may retain data due to wear-leveling and over-provisioning',
      details: 'Solid State Drives (SSDs) use wear-leveling algorithms that distribute writes across all memory cells. This means your data may be moved to different physical locations without your knowledge. Additionally, most SSDs maintain spare capacity (over-provisioning) that isn\'t accessible to normal secure deletion tools. For SSDs, consider using the manufacturer\'s secure erase command or physical destruction for highly sensitive data.',
      mitigation: [
        'Use manufacturer secure erase tools',
        'Enable full disk encryption',
        'Consider physical destruction for decommissioned SSDs'
      ]
    },
    {
      id: 'cloud',
      title: 'Cloud & Network Storage',
      severity: 'medium',
      icon: <Cpu size={18} />,
      summary: 'Cloud-synced files may have remote copies unaffected by local deletion',
      details: 'Files synchronized to cloud services (Google Drive, OneDrive, Dropbox, etc.) maintain copies on remote servers. Deleting a file locally doesn\'t necessarily remove all cloud copies immediately. Many services maintain version history or "trash" systems that preserve deleted files for extended periods.',
      mitigation: [
        'Disable cloud sync before deletion',
        'Use provider\'s wipe tools',
        'Manually delete from all devices and cloud',
        'Verify deletion through web interface'
      ]
    },
    {
      id: 'filesystem',
      title: 'File System Artifacts',
      severity: 'medium',
      icon: <FileText size={18} />,
      summary: 'Modern file systems may preserve data in journals and logs',
      details: 'File systems like NTFS, APFS, and ext4 maintain journals, transaction logs, and sometimes shadow copies that can preserve fragments of deleted files. Some systems like Windows maintain previous versions through Volume Shadow Copy. These features are designed for data recovery but can undermine secure deletion efforts.',
      mitigation: [
        'Disable system restore points',
        'Clear file system journals before deletion',
        'Use raw device access for critical deletions',
        'Consider full disk wipe when possible'
      ]
    }
  ];

  const bestPractices = [
    {
      id: 'classification',
      title: 'Data Classification Policy',
      icon: <Shield size={18} />,
      summary: 'Implement tiered deletion based on data sensitivity',
      details: 'Create a data classification policy that defines different security levels for your information. Match deletion methods to these classifications:',
      levels: [
        {
          name: 'Public',
          method: 'Quick delete',
          description: 'No secure deletion needed for non-sensitive data'
        },
        {
          name: 'Internal',
          method: 'DoD 3-pass',
          description: 'Standard protection for business documents'
        },
        {
          name: 'Confidential',
          method: '7-pass forensic',
          description: 'For sensitive business information'
        },
        {
          name: 'Top Secret',
          method: 'Gutmann + physical',
          description: 'Maximum protection for critical data'
        }
      ]
    },
    {
      id: 'verification',
      title: 'Deletion Verification',
      icon: <CheckCircle size={18} />,
      summary: 'Always verify important deletions',
      details: 'For critical deletions, verification is essential. Consider these methods:',
      methods: [
        'Use hex editors to inspect disk areas',
        'Employ forensic recovery tools to test deletion',
        'For full disks, compare checksums before/after',
        'Use professional verification services for compliance'
      ]
    },
    {
      id: 'compliance',
      title: 'Regulatory Compliance',
      icon: <FileText size={18} />,
      summary: 'Understand your legal requirements',
      details: 'Different industries have different requirements for data destruction:',
      regulations: [
        {
          name: 'GDPR',
          requirement: 'Right to erasure',
          method: 'Certified deletion methods'
        },
        {
          name: 'HIPAA',
          requirement: 'PHI destruction',
          method: 'NIST 800-88 or equivalent'
        },
        {
          name: 'PCI DSS',
          requirement: 'Cardholder data',
          method: 'Irreversible deletion'
        }
      ]
    }
  ];

  const toggleAlgorithm = (algorithm) => {
    setExpandedAlgorithm(expandedAlgorithm === algorithm ? null : algorithm);
  };

  const toggleRisk = (riskId) => {
    setExpandedRisk(expandedRisk === riskId ? null : riskId);
  };

  const togglePractice = (practiceId) => {
    setExpandedPractice(expandedPractice === practiceId ? null : practiceId);
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-2xl border border-gray-700 w-full max-w-4xl max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 px-6 py-4 border-b border-gray-700 flex justify-between items-center">
        <div className="flex items-center">
          <HelpCircle className="w-5 h-5 text-blue-400 mr-2" />
          <h2 className="text-xl font-semibold text-white">SecureErase Pro Help Center</h2>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>
      
      {/* Tabs */}
      <div className="bg-gray-850 border-b border-gray-700">
        <div className="flex overflow-x-auto">
          <button 
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'algorithms' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('algorithms')}
          >
            Deletion Methods
          </button>
          <button 
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'risks' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('risks')}
          >
            Risks & Limitations
          </button>
          <button 
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'practices' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('practices')}
          >
            Best Practices
          </button>
          <button 
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'faq' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('faq')}
          >
            FAQ
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6 max-h-[450px] overflow-y-auto">
        {activeTab === 'algorithms' && (
          <div className="space-y-3">
            <p className="text-gray-300 mb-4 text-sm">
              Select a deletion method to learn about its security level, appropriate use cases, and technical details.
            </p>
            
            {Object.entries(deleteModesConfig).map(([key, algorithm]) => (
              <div key={key} className="bg-gray-750 border border-gray-700 rounded-lg overflow-hidden">
                <button
                  className={`w-full text-left p-4 flex justify-between items-center ${
                    expandedAlgorithm === key ? 'bg-gray-700' : 'hover:bg-gray-700/50'
                  } transition-colors`}
                  onClick={() => toggleAlgorithm(key)}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-md ${algorithm.color} bg-opacity-20 mr-3`}>
                      {algorithm.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{algorithm.label}</h3>
                      <p className="text-xs text-gray-400 mt-1">{algorithm.description}</p>
                    </div>
                  </div>
                  {expandedAlgorithm === key ? (
                    <ChevronUp className="text-gray-400" size={18} />
                  ) : (
                    <ChevronDown className="text-gray-400" size={18} />
                  )}
                </button>
                
                {expandedAlgorithm === key && (
                  <div className="p-4 border-t border-gray-700 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Security Level</h4>
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full ${algorithm.color} mr-2`}></div>
                          <span className="text-xs">
                            {algorithm.level === 'G' ? 'Maximum' : 
                             algorithm.level === 'F' ? 'High' : 
                             algorithm.level === 'M' ? 'Medium' : 'Standard'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Passes</h4>
                        <p className="text-xs">{algorithm.passes} overwrite pass{algorithm.passes !== 1 ? 'es' : ''}</p>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Technical Details</h4>
                      <p className="text-xs text-gray-400">{algorithm.details}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Recommended For</h4>
                        <ul className="text-xs text-gray-400 space-y-1">
                          {algorithm.useCases.map((useCase, i) => (
                            <li key={i} className="flex items-start">
                              <span className="text-green-400 mr-1">✓</span> {useCase}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Limitations</h4>
                        <ul className="text-xs text-gray-400 space-y-1">
                          {algorithm.limitations.map((limitation, i) => (
                            <li key={i} className="flex items-start">
                              <span className="text-red-400 mr-1">✗</span> {limitation}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    {algorithm.compliance && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Compliance</h4>
                        <div className="flex flex-wrap gap-2">
                          {algorithm.compliance.map((standard, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-gray-700 rounded-full">
                              {standard}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'risks' && (
          <div className="space-y-3">
            <p className="text-gray-300 mb-4 text-sm">
              Understanding these risks will help you choose the appropriate deletion method for your situation.
            </p>
            
            {riskFactors.map((risk) => (
              <div key={risk.id} className="bg-gray-750 border border-gray-700 rounded-lg overflow-hidden">
                <button
                  className={`w-full text-left p-4 flex justify-between items-center ${
                    expandedRisk === risk.id ? 'bg-gray-700' : 'hover:bg-gray-700/50'
                  } transition-colors`}
                  onClick={() => toggleRisk(risk.id)}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-md ${
                      risk.severity === 'high' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                    } mr-3`}>
                      {risk.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{risk.title}</h3>
                      <p className="text-xs text-gray-400 mt-1">{risk.summary}</p>
                    </div>
                  </div>
                  {expandedRisk === risk.id ? (
                    <ChevronUp className="text-gray-400" size={18} />
                  ) : (
                    <ChevronDown className="text-gray-400" size={18} />
                  )}
                </button>
                
                {expandedRisk === risk.id && (
                  <div className="p-4 border-t border-gray-700 space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Detailed Explanation</h4>
                      <p className="text-xs text-gray-400">{risk.details}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Mitigation Strategies</h4>
                      <ul className="text-xs text-gray-400 space-y-2">
                        {risk.mitigation.map((item, i) => (
                          <li key={i} className="flex items-start">
                            <span className="text-blue-400 mr-2">→</span> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {risk.severity === 'high' && (
                      <div className="bg-red-900/20 p-3 rounded border border-red-800/50">
                        <div className="flex items-start">
                          <AlertTriangle className="text-red-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                          <p className="text-xs text-red-100">
                            This is a high severity risk that requires special attention for sensitive data.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'practices' && (
          <div className="space-y-3">
            <p className="text-gray-300 mb-4 text-sm">
              Follow these best practices to ensure effective data sanitization.
            </p>
            
            {bestPractices.map((practice) => (
              <div key={practice.id} className="bg-gray-750 border border-gray-700 rounded-lg overflow-hidden">
                <button
                  className={`w-full text-left p-4 flex justify-between items-center ${
                    expandedPractice === practice.id ? 'bg-gray-700' : 'hover:bg-gray-700/50'
                  } transition-colors`}
                  onClick={() => togglePractice(practice.id)}
                >
                  <div className="flex items-center">
                    <div className="p-2 rounded-md bg-blue-500/20 mr-3">
                      {practice.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{practice.title}</h3>
                      <p className="text-xs text-gray-400 mt-1">{practice.summary}</p>
                    </div>
                  </div>
                  {expandedPractice === practice.id ? (
                    <ChevronUp className="text-gray-400" size={18} />
                  ) : (
                    <ChevronDown className="text-gray-400" size={18} />
                  )}
                </button>
                
                {expandedPractice === practice.id && (
                  <div className="p-4 border-t border-gray-700 space-y-4">
                    <p className="text-xs text-gray-400">{practice.details}</p>
                    
                    {practice.levels && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-gray-300">Implementation Levels</h4>
                        <div className="grid gap-3">
                          {practice.levels.map((level, i) => (
                            <div key={i} className="bg-gray-800/50 p-3 rounded border border-gray-700">
                              <h5 className="text-xs font-medium text-white">{level.name}</h5>
                              <p className="text-xs text-gray-400 mt-1">
                                <span className="text-blue-400">{level.method}:</span> {level.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {practice.methods && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-300">Recommended Methods</h4>
                        <ul className="text-xs text-gray-400 space-y-2">
                          {practice.methods.map((method, i) => (
                            <li key={i} className="flex items-start">
                              <span className="text-green-400 mr-2">✓</span> {method}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {practice.regulations && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-300">Regulatory Requirements</h4>
                        <div className="grid gap-2">
                          {practice.regulations.map((reg, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-gray-300">{reg.name}</span>
                              <span className="text-gray-400">{reg.requirement}</span>
                              <span className="text-blue-400">{reg.method}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'faq' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white mb-3">Frequently Asked Questions</h3>
            
            <div className="space-y-3">
              <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-white">What's the difference between deletion and secure deletion?</h4>
                <p className="text-xs text-gray-400 mt-2">
                  Normal deletion just removes file references while secure deletion overwrites the actual data. Regular delete leaves data recoverable, secure delete makes recovery extremely difficult.
                </p>
              </div>
              
              <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-white">How do I choose the right deletion method?</h4>
                <p className="text-xs text-gray-400 mt-2">
                  Consider your data sensitivity - use Quick Erase for temporary files, DoD 3-pass for personal data, and Gutmann for highly sensitive information. Our algorithm guide can help you decide.
                </p>
              </div>
              
              <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-white">Is secure deletion effective on SSDs?</h4>
                <p className="text-xs text-gray-400 mt-2">
                  SSDs require special consideration due to wear-leveling. While secure deletion helps, for maximum security on SSDs use manufacturer secure erase tools or full disk encryption.
                </p>
              </div>
              
              <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-white">How can I verify data was securely deleted?</h4>
                <p className="text-xs text-gray-400 mt-2">
                  Use forensic recovery tools to attempt recovery, inspect disk sectors with hex editors, or for compliance scenarios, hire professional verification services.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="bg-gray-850 px-6 py-4 border-t border-gray-700 flex justify-between items-center">
        <button className="text-sm text-gray-400 hover:text-blue-400 flex items-center">
          <Info className="w-4 h-4 mr-1" />
          Need more help?
        </button>
        <button 
          onClick={onClose}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors duration-200"
        >
          Close Help
        </button>
      </div>
    </div>
  );
};

export default SecureEraseHelpCenter;